import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { ListMagnifyingGlass, MapTrifold } from '@phosphor-icons/react'
import { Table } from 'react-bootstrap'
import AppChip from '../../../components/chips/AppChip'
import UbiquaTablePagination from '../../../components/table/UbiquaTablePagination'
import { MobilePaginationBar } from '../../../components/mobile'
import ActivityBreakdownMobileContent from '../mobile/ActivityBreakdownMobileContent'
import { getGeofenceStatus } from '../helpers/geofenceStatus'
import { VISIT_ACTIVITY_CHIP_VARIANT } from '../helpers/visitChipVariant'

const TABLE_HEADERS = [
  { label: 'Fecha y hora', description: 'Fecha y hora de la actividad registrada.' },
  { label: 'Cliente', description: 'Código y nombre del cliente.' },
  { label: 'Actividad', description: 'Tipo de actividad registrada.' },
  { label: 'Motivo/Descripción', description: 'Motivo o descripción de la actividad.' },
  { label: 'Monto', description: 'Monto asociado a la actividad.', align: 'center' },
  {
    label: 'Dentro de geocerca',
    description: 'Indica si la actividad fue dentro de geocerca.',
    align: 'center'
  },
  { label: 'Ubicación', description: 'Abre el mapa de la actividad.', align: 'center' }
]

const CENTER_HEADERS = new Set(['Monto', 'Dentro de geocerca', 'Ubicación'])

export default function ActivityBreakdownTable({
  rows = [],
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  perPage = 25,
  onPageChange,
  onRowsPerPageChange,
  onPaginate,
  onTransactionClick,
  onOpenLocation,
  formatActivityName,
  isMobileLayout = false,
  hidePagination = false,
  expandedActivityKey = null,
  onToggleExpand
}) {
  const paginationTotalCount = Math.max(0, Number(totalCount) || rows.length)
  const shouldShowPagination = !hidePagination && !isMobileLayout && paginationTotalCount > 10

  const towerPaginationNode = shouldShowPagination ? (
    <UbiquaTablePagination
      ariaLabel="Paginación de actividades"
      itemLabel="actividades"
      emptyLabel="Sin actividades para mostrar"
      showPageSizeControl
      disabled={isLoading}
      pagination={{
        total_count: paginationTotalCount,
        per_page: perPage,
        total_pages: Math.max(1, totalPages || 1),
        current_page: currentPage
      }}
      onPageChange={(_, page) => onPageChange?.(page)}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  ) : null

  const towerEmptyState = (
    <Box
      sx={{
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 4
      }}
    >
      <Box sx={{ maxWidth: 360 }}>
        <Box
          sx={{
            width: 66,
            height: 66,
            borderRadius: '50%',
            backgroundColor: '#F2F6FB',
            color: '#8FA4BC',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.75
          }}
        >
          <ListMagnifyingGlass size={34} weight="regular" />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A2733', mb: 1 }}>
          No hay actividades para mostrar
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.55, color: '#6B7A8C' }}>
          No encontramos actividades con los filtros actuales. Prueba ajustando la búsqueda o
          restableciendo los filtros.
        </Typography>
      </Box>
    </Box>
  )

  if (isMobileLayout) {
    return (
      <>
        {isLoading ? (
          <Stack spacing={1.5} aria-busy="true">
            <span role="status" className="visually-hidden">
              Cargando actividades
            </span>
            {Array.from({ length: rows.length || perPage }, (_, index) => (
              <Box
                key={index}
                aria-hidden="true"
                sx={{ p: 2, border: '1px solid #edf1f6', borderRadius: 2 }}
              >
                <Skeleton animation="wave" variant="rounded" width="68%" height={14} />
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width="40%"
                  height={10}
                  sx={{ mt: 1 }}
                />
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Skeleton animation="wave" variant="rounded" width="32%" height={12} />
                  <Skeleton animation="wave" variant="rounded" width="24%" height={20} />
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <ActivityBreakdownMobileContent
            data={rows.map((row) => row.rawRow)}
            expandedActivityKey={expandedActivityKey}
            onToggleExpand={onToggleExpand}
            formatActivityName={formatActivityName}
            onCodeClick={onTransactionClick}
            onOpenLocation={onOpenLocation}
          />
        )}
        {!hidePagination && totalPages > 1 ? (
          <Box sx={{ mt: 1 }}>
            <MobilePaginationBar
              current_page={currentPage}
              total_pages={totalPages}
              disablePaper
              handle_pagination={onPaginate}
            />
          </Box>
        ) : null}
      </>
    )
  }

  return (
    <div className="route-tower-table-shell">
      <div className="route-tower-table-card" aria-busy={isLoading}>
        {isLoading ? (
          <span role="status" className="visually-hidden">
            Cargando actividades
          </span>
        ) : null}
        <div className="route-tower-scroll">
          <Table hover responsive className="route-tower-table">
            <thead className="table-header-gray-mui">
              <tr>
                {TABLE_HEADERS.map((header) => (
                  <th
                    key={header.label}
                    className={CENTER_HEADERS.has(header.label) ? 'align-center-table-column' : ''}
                  >
                    <Tooltip title={header.description} placement="top" arrow>
                      <div
                        className={`table-details-header-container ${
                          CENTER_HEADERS.has(header.label) ? 'justify-content-center' : ''
                        }`}
                      >
                        <span className="th-header-text">{header.label.toUpperCase()}</span>
                        <FontAwesomeIcon
                          icon={faQuestionCircle}
                          className="table-header-help-icon"
                        />
                      </div>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: rows.length || perPage }, (_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
                    {TABLE_HEADERS.map((header, columnIndex) => {
                      const isCentered = CENTER_HEADERS.has(header.label)
                      const width = [72, 86, 64, 80][(rowIndex + columnIndex) % 4]
                      return (
                        <td key={header.label} style={{ height: 58 }}>
                          <Skeleton
                            animation="wave"
                            variant="rounded"
                            width={isCentered ? 58 : `${width}%`}
                            height={isCentered ? 18 : 12}
                            sx={{
                              borderRadius: isCentered ? '999px' : undefined,
                              mx: isCentered ? 'auto' : 0
                            }}
                          />
                        </td>
                      )
                    })}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr className="route-tower-empty-row">
                  <td colSpan={TABLE_HEADERS.length} style={{ padding: 0 }}>
                    {towerEmptyState}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.rowKey}>
                    <td
                      className="td-text-limited route-cell-tabular"
                      onClick={(event) => onTransactionClick?.(event, row.rawRow)}
                    >
                      {row.createdDateLabel}
                    </td>
                    <td
                      className="td-text-limited"
                      onClick={(event) => onTransactionClick?.(event, row.rawRow)}
                    >
                      <Tooltip title={row.clientLabel} placement="top" arrow>
                        <span className="route-client-cell">
                          <span className="route-client-name">{row.clientName}</span>
                          <span className="route-client-code">{row.clientUid}</span>
                        </span>
                      </Tooltip>
                    </td>
                    <td
                      className="td-text-limited"
                      onClick={(event) => onTransactionClick?.(event, row.rawRow)}
                      style={
                        row.isErrorActivity
                          ? { maxWidth: 300, color: '#FF2323' }
                          : { maxWidth: 300 }
                      }
                    >
                      <AppChip
                        label={row.activityLabel}
                        variant={row.isErrorActivity ? 'danger' : VISIT_ACTIVITY_CHIP_VARIANT}
                      />
                    </td>
                    <td
                      className="td-text-limited"
                      onClick={(event) => onTransactionClick?.(event, row.rawRow)}
                    >
                      {row.motiveText}
                    </td>
                    <td
                      className="td-text-limited align-center-table-column route-cell-tabular"
                      onClick={(event) => onTransactionClick?.(event, row.rawRow)}
                    >
                      {row.totalLabel}
                    </td>
                    <td className="td-text-limited align-center-table-column">
                      {row.isLiquidation ? (
                        '-'
                      ) : (
                        <AppChip {...getGeofenceStatus(row.geofenceLabel)} />
                      )}
                    </td>
                    <td className="td-text-limited align-center-table-column">
                      {row.isLiquidation ? (
                        '-'
                      ) : (
                        <Tooltip title="Ver ubicación" placement="top" arrow>
                          <span>
                            <button
                              type="button"
                              className="route-location-action"
                              aria-label="Ver ubicación"
                              onClick={(event) => onOpenLocation?.(row.rawRow, event)}
                            >
                              <MapTrifold size={20} weight="regular" aria-hidden="true" />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
      {towerPaginationNode}
    </div>
  )
}
